import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { useExportDownloadInFlight } from '@verantos-dev/core-utils';

import { analysisExportDownload } from '../api/analysisExportDownload';
import type { UseAnalysisDownloadArgs, UseAnalysisDownloadResult } from '../types';

export const useAnalysisDownload = ({
  projectId,
  analysisProfileId,
  analysisName,
  latestStatus,
}: UseAnalysisDownloadArgs): UseAnalysisDownloadResult => {
  const { formatMessage } = useIntl();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const download = analysisExportDownload({ projectId, analysisProfileId, analysisName });
  // The fetch survives the modal closing — while it runs in the background the
  // button stays disabled so a second click can't start a duplicate export.
  const isDownloadInFlight = useExportDownloadInFlight(download.key);
  // The profile's raw status, not the header badge: the badge describes the
  // definition, and the download needs a processed run.
  const canDownload = latestStatus === 'PROCESSED';

  const downloadTooltip = isDownloadInFlight
    ? formatMessage({ id: 'analysisDetail.downloadTooltip.inFlight' })
    : canDownload
    ? ''
    : formatMessage({ id: 'analysisDetail.downloadTooltip.notProcessed' });

  const onDownload = useCallback(() => setIsDownloadModalOpen(true), []);
  const onCloseDownloadModal = useCallback(() => setIsDownloadModalOpen(false), []);

  return {
    download,
    isDownloadModalOpen,
    isDownloadDisabled: isDownloadModalOpen || !canDownload || isDownloadInFlight,
    downloadTooltip,
    onDownload,
    onCloseDownloadModal,
  };
};
